"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PositionPopup = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const constants_1 = require("../../constants");
const nano_theme_1 = require("nano-theme");
const context_1 = require("./context");
const useMountedState_1 = tslib_1.__importDefault(require("react-use/lib/useMountedState"));
const portal_1 = require("../portal");
const context_2 = require("../../context");
const positionClass = (0, nano_theme_1.rule)({
    d: 'block',
    pos: 'fixed',
    z: constants_1.ZINDEX.CONTEXT,
});
const positionClassWithFadeIn = (0, nano_theme_1.rule)({
    an: 'fadeInScaleIn .04s',
});
const positionClassWithAnimation = (0, nano_theme_1.rule)({
    trs: 'left .1s cubic-bezier(0,0,0,1), top .1s cubic-bezier(0,0,0,1)',
});
/**
 * Places popup at the anchor point.
 */
const PositionPopup = ({ fadeIn, animate, children }) => {
    const point = (0, context_1.useAnchorPoint)();
    const isMounted = (0, useMountedState_1.default)();
    const timer = React.useRef(null);
    const ro = React.useRef(null);
    const elRef = React.useRef(null);
    const hidden = (0, context_2.useHiddenTrace)();
    const applyStyle = React.useCallback(() => {
        const el = elRef.current;
        if (!el || !point)
            return;
        const style = point.style();
        const s = el.style;
        for (const key in style)
            s[key] = style[key];
        // Inherit writing direction from the toggle so portaled content respects RTL/LTR.
        const toggle = point.toggle;
        if (toggle) {
            const dir = getComputedStyle(toggle).direction;
            if (dir && el.dir !== dir)
                el.dir = dir;
        }
    }, [point]);
    /**
     * Position the popup in a layout effect rather than inside the ref callback.
     *
     * React guarantees that ALL refs in a render batch are assigned before any
     * useLayoutEffect fires. By the time this runs, the toggle element's ref
     * (set by the ancestor BasicTooltip / PopupControlled) is already populated
     * on the AnchorPointHandle, so point.style() can compute the correct position.
     * This avoids the position:0,0 flash that would occur if we tried to
     * position inside the ref callback, where sibling/ancestor refs are not yet set.
     *
     * useLayoutEffect runs synchronously before the browser paints, so there is
     * no visible flash of mis-positioned content.
     */
    React.useLayoutEffect(() => {
        applyStyle();
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when hidden changes; applyStyle/isMounted are stable refs
    React.useEffect(() => {
        if (!hidden) {
            requestAnimationFrame(() => {
                if (!isMounted())
                    return;
                setTimeout(() => {
                    if (!isMounted())
                        return;
                    applyStyle();
                }, 100);
            });
        }
    }, [hidden]);
    const ref = React.useCallback((el) => {
        elRef.current = el;
        if (!el) {
            ro.current?.disconnect();
            return;
        }
        ro.current = new ResizeObserver(() => {
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
            timer.current = setTimeout(() => {
                if (!isMounted())
                    return;
                applyStyle();
            }, 50);
        });
        ro.current.observe(el);
    }, [applyStyle, isMounted]);
    if (hidden)
        return;
    return (React.createElement(portal_1.Portal, null,
        React.createElement("div", { ref: ref, className: positionClass + (fadeIn ? positionClassWithFadeIn : '') + (animate ? positionClassWithAnimation : '') }, children)));
};
exports.PositionPopup = PositionPopup;
